import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Check, CircleHelp, Clock, MapPin, Repeat, Users, Video, X, Ban } from 'lucide-react';
import { enlaceMeet } from '../../lib/meetEnlace';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import {
  ESTADOS, ZONA_DEFECTO, esUrl, mensajeError, textoDia, ymdDe,
} from '../../lib/calendario';
import '../calendar/cal.css';

const TEXTO_RESPUESTA = { aceptado: 'aceptó', rechazado: 'rechazó', quizas: 'respondió «quizás» a', pendiente: 'aún no responde a' };

/**
 * Tarjeta de invitación sobre el cuerpo de un correo con iCalendar (`msg.invitacion`). Lee la invitación con
 * `GET /calendar/invitacion` (que además aplica sola una RESPUESTA a un evento mío o una CANCELACIÓN) y en una
 * invitación (REQUEST) deja responder Sí/Quizás/No: el evento entra al calendario y el organizador recibe la
 * respuesta desde este buzón. No toca el cuerpo del correo (que sigue en su iframe aislado).
 */
export default function TarjetaInvitacion({ accountId, folder, uid }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [enviando, setEnviando] = useState(false);
  const clave = ['cal-invitacion', accountId, folder, String(uid)];
  const { data, isLoading, isError } = useQuery({
    queryKey: clave,
    queryFn: async () => (await api.get('/calendar/invitacion', { params: { account_id: accountId, folder, uid } })).data,
    enabled: !!accountId && !!uid,
    staleTime: 60000,
    retry: false,
  });
  const { data: cals } = useQuery({
    queryKey: ['cal-calendarios'],
    queryFn: async () => (await api.get('/calendar/calendars')).data,
    staleTime: 300000,
  });
  const tz = cals?.zona || ZONA_DEFECTO;

  if (isLoading) {
    return <div className="cal-tarjeta-inv" data-testid="invitacion-cargando"><div className="cal-tarjeta-inv-cab"><span className="cal-ayuda">Leyendo la invitación…</span></div></div>;
  }
  if (isError || !data) return null;

  const dia = data.todo_el_dia ? data.fecha_inicio : (data.inicio ? ymdDe(data.inicio, tz) : null);
  const cancelado = data.cancelado || data.metodo === 'CANCEL';
  const responder = async (estado) => {
    setEnviando(true);
    try {
      const { data: r } = await api.post('/calendar/invitacion/responder', { account_id: accountId, folder, uid: String(uid), estado });
      qc.setQueryData(clave, (d) => (d ? { ...d, mi_estado: estado, evento_id: r.evento_id || d.evento_id } : d));
      qc.invalidateQueries({ queryKey: ['cal-eventos'] });
      toast.success({ aceptado: 'Aceptada: ya está en tu calendario', quizas: 'Respondiste «quizás»', rechazado: 'Rechazada' }[estado]);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo responder la invitación'));
    } finally {
      setEnviando(false);
    }
  };

  const invitados = data.invitados || [];
  const org = data.organizador || {};

  return (
    <div className={`cal-tarjeta-inv ${cancelado ? 'is-cancelada' : ''}`} data-testid="invitacion-tarjeta">
      <div className="cal-tarjeta-inv-cab">
        {dia ? (
          <div className="cal-tarjeta-inv-fecha" aria-hidden="true">
            <span>{textoDia(dia, { month: 'short' }).replace('.', '')}</span>
            <span>{Number(dia.slice(8, 10))}</span>
          </div>
        ) : <CalendarDays className="h-8 w-8" style={{ color: 'var(--accent)' }} />}
        <div className="min-w-0 flex-1">
          <p className="cal-ayuda" style={{ fontWeight: 600, color: cancelado ? 'var(--danger)' : 'var(--accent)' }}>
            {cancelado ? 'Evento cancelado' : data.metodo === 'REPLY' ? 'Respuesta a una invitación' : 'Invitación'}
          </p>
          <h3 className="cal-tarjeta-inv-titulo" style={{ textDecoration: cancelado ? 'line-through' : undefined }}>{data.titulo || '(Sin título)'}</h3>
          {data.cuando && <div className="cal-tarjeta-inv-dato"><Clock />{data.cuando}</div>}
          {data.repeticion && <div className="cal-tarjeta-inv-dato"><Repeat />{data.repeticion}</div>}
          {enlaceMeet(data.ubicacion, data.descripcion) && (
            <div className="cal-tarjeta-inv-dato" data-testid="invitacion-meet">
              <Video />
              <a className="cal-meet-boton" href={enlaceMeet(data.ubicacion, data.descripcion).url} target="_blank" rel="noopener noreferrer">Unirse con Orvian Meet</a>
            </div>
          )}
          {data.ubicacion && (
            <div className="cal-tarjeta-inv-dato"><MapPin />
              {esUrl(data.ubicacion) ? <a className="cal-enlace break-all" href={data.ubicacion} target="_blank" rel="noopener noreferrer">{data.ubicacion}</a> : <span className="break-words">{data.ubicacion}</span>}
            </div>
          )}
          {(org.correo || invitados.length > 0) && (
            <div className="cal-tarjeta-inv-dato"><Users />
              <span className="min-w-0">
                {org.correo && <>Organiza {org.nombre || org.correo}</>}
                {invitados.length > 0 && <>{org.correo ? ' · ' : ''}{invitados.length} {invitados.length === 1 ? 'invitado' : 'invitados'}: {invitados.slice(0, 4).map((i) => `${i.nombre || i.correo}${i.estado && i.estado !== 'pendiente' ? ` (${ESTADOS[i.estado]})` : ''}`).join(', ')}{invitados.length > 4 ? '…' : ''}</>}
              </span>
            </div>
          )}
        </div>
      </div>

      {data.metodo === 'REPLY' ? (
        <div className="cal-tarjeta-inv-pie">
          <span className="text-[13px]" style={{ color: 'var(--text)' }}>
            {data.respuesta
              ? <><b>{data.respuesta.nombre || data.respuesta.correo}</b> {TEXTO_RESPUESTA[data.respuesta.estado] || 'respondió a'} la invitación{data.aplicada ? '. Ya se anotó en tu evento.' : '.'}</>
              : 'Respuesta de un invitado.'}
          </span>
          <span className="flex-1" />
          {data.evento_id && <button type="button" className="od-btn-secondary" onClick={() => nav(`/calendar?evento=${data.evento_id}`)}>Ver en el calendario</button>}
        </div>
      ) : cancelado ? (
        <div className="cal-tarjeta-inv-pie">
          <Ban className="h-4 w-4" style={{ color: 'var(--danger)' }} />
          <span className="text-[13px]" style={{ color: 'var(--text)' }}>
            El organizador canceló este evento{data.aplicada ? ' y se marcó como cancelado en tu calendario' : ''}.
          </span>
        </div>
      ) : (
        <div className="cal-tarjeta-inv-pie" data-testid="invitacion-responder">
          <span className="text-[13px] mr-1" style={{ color: 'var(--text-2)' }}>
            {data.mi_estado && data.mi_estado !== 'pendiente' ? `Tu respuesta: ${ESTADOS[data.mi_estado]}` : '¿Asistirás?'}
          </span>
          {[['aceptado', 'Sí', Check], ['quizas', 'Quizás', CircleHelp], ['rechazado', 'No', X]].map(([estado, texto, Ico]) => (
            <button
              key={estado}
              type="button"
              className={`cal-rsvp ${data.mi_estado === estado ? 'is-activa' : ''}`}
              disabled={enviando}
              onClick={() => responder(estado)}
              aria-pressed={data.mi_estado === estado}
              data-testid={`invitacion-${estado}`}
            >
              <Ico className="h-3.5 w-3.5" />{texto}
            </button>
          ))}
          <span className="flex-1" />
          {data.evento_id && <button type="button" className="cal-mini-enlace" onClick={() => nav(`/calendar?evento=${data.evento_id}`)}>Ver en el calendario</button>}
        </div>
      )}
    </div>
  );
}
